<template>
    <div class="searchableTextFilterPanel">
        <div v-for="(item,i) in rules" :key="i">
            <div class="filterRuleRow">
                <div class="row items-center no-wrap q-col-gutter-xs">
                    <div class="col-12 col-sm-4">
                        <q-select outlined dense hide-bottom-space bg-color="white"
                                :label="Translate('Operator')"
                                v-model="item.condition"
                                :options="condition_types"
                                option-label="text"
                                option-value="value"
                                emit-value
                                map-options
                                @update:model-value="updateReport" />
                    </div>
                    <div class="col-12 col-sm-8">
                        <q-input outlined dense hide-bottom-space bg-color="white"
                                :label="Translate('Value')"
                                v-model="item.value"
                                :disable="item.condition == 'empty' || item.condition == 'notempty' || item.condition == 'missing'"
                                @update:model-value="updateReport" />
                    </div>
                </div>

                <q-btn v-if="i > 0" size="md" text-color="grey-7" flat round dense icon="mdi-window-close" class="filterRemoveBtn" @click="removeCondition()" />
            </div>

            <div class="row justify-center q-my-none" v-if="i < 1" v-show="rules.length > 1">
                <div class="q-my-sm row items-center spacing-xs">
                    <q-radio class="q-mx-xs" v-model="item.combiner" val="AND" :label="Translate('AND')" @update:model-value="updateReport" />
                    <q-radio class="q-mx-xs" v-model="item.combiner" val="OR" :label="Translate('OR')" @update:model-value="updateReport" />
                </div>
            </div>
        </div>
        <div>
            <q-btn v-show="rules.length < 2" flat dense no-caps unelevated class="q-px-sm q-my-sm customHoverButton c-SentenceCaseButton" @click="addCondition()">
                <q-icon name="mdi-plus" size="xs" class="q-mr-xs" />
                <span>{{ Translate('Add Rule') }}</span>
            </q-btn>
        </div>
    </div>
</template>

<script>
    import _ from 'lodash';
    import methods from '@/Shared/methods';

    export default {
        // Required in Vue 3 so parent @change is the component emit, not a native
        // DOM listener — otherwise blur/click on the grid delivers an Event and
        // localUpdateFilterValue treats Event.type as filter criteria (CCDEV-18136).
        emits: ['change'],
        data: function () {
            return {
                rules: [{ condition: 'contains', value: '', combiner: undefined }],
            }
        },
        props: {
            item: Object,
            value: null,
        },
        created() {
            const self = this;
            this.updateReportDebouncer = _.debounce(function () {
                // Match Vue2: empty value (or no rules) means no criteria — emit {} so
                // localUpdateFilterValue resets to { usage } / cleared and stops reducing rows.
                if (self.rules.length < 1 || self.rules.some(a => !a.value && a.condition != 'empty' && a.condition != 'notempty' && a.condition != 'missing')) {
                    self.$emit('change', {});
                    return;
                }

                if (self.rules.length == 1)
                    self.$emit('change', {
                        filterType: "text",
                        type: self.rules[0].condition,
                        filter: self.rules[0].value,
                    });
                else
                    self.$emit('change', {
                        filterType: "text",
                        operator: self.rules[0].combiner,
                        conditions: self.rules.map(a => ({
                            type: a.condition,
                            filter: a.value,
                        }))
                    });

            }, 500);

            if (this.value && this.value.type) {
                this.rules = [{
                    condition: this.value.type,
                    value: this.value.filter,
                }];
            }
            else if (this.value && this.value.conditions && this.value.conditions.length == 2) {
                this.rules = [{
                    condition: this.value.conditions[0].type,
                    value: this.value.conditions[0].filter,
                    combiner: this.value.operator,
                }, {
                    condition: this.value.conditions[1].type,
                    value: this.value.conditions[1].filter,
                }];
            }
        },
        watch: {
            rules: function (newv) {
                this.updateReport();
            },
            value: {
                handler(newValue) {
                    this.syncFromProps(newValue);
                },
                deep: true,
                immediate: true 
            },
        },
        computed: {
            condition_types: function () {
                return [
                    { text: this.Translate('Equals'), value: 'equals' },
                    { text: this.Translate('Does Not Equal'), value: 'notEquals' },
                    { text: this.Translate('Contains'), value: 'contains' },
                    { text: this.Translate('Does Not Contain'), value: 'notcontains' },
                    { text: this.Translate('Begins With'), value: 'beginsith' },
                    { text: this.Translate('Ends With'), value: 'endswith' },
                    { text: this.Translate('Blank'), value: 'empty' },
                    { text: this.Translate('Not Blank'), value: 'notempty' },
                    { text: this.Translate('Missing'), value: 'missing' },
                ];
            },
        },
        methods: {
            ...methods,
            isIncompleteRule(rule) {
                return !rule.value && rule.condition != 'empty' && rule.condition != 'notempty' && rule.condition != 'missing';
            },
            // CCDEV-18163: clearing emits {} (keeps 18136 row-filter reset) which re-asserts
            // usage/cleared on the parent. Do not wipe operator/value rows that are still
            // incomplete drafts (empty Value, or a row just added via Add Rule).
            syncFromProps(value) {
                if (!value || Object.keys(value).length === 0 || value.cleared == 1 || (value.filterType === 'set' && (!value.values || value.values.length === 0))) {
                    if (this.rules.length > 0 && this.rules.some(a => this.isIncompleteRule(a)))
                        return;
                    if (this.rules.length > 0)
                        this.rules = [];
                }
            },
            updateReport() {
                this.updateReportDebouncer();
            },
            addCondition() {
                if (this.rules.length === 1) {
                    this.rules[0].combiner = this.rules[0].combiner || 'AND';
                }
                this.rules.push({ condition: 'contains', value: '', combiner: undefined });
                this.updateReport();
            },
            removeCondition() {
                this.rules.pop();
                this.updateReport();
            }
        }
    }
</script>

<style scoped>
    .searchableTextFilterPanel {
        padding: 8px 32px 12px 16px;
        margin-top: 4px;
    }
    .filterRuleRow {
        position: relative;
    }

    .filterRemoveBtn {
        position: absolute;
        right: -36px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;
    }

    .customHoverButton:before {
        background-color: unset !important;
        transition: unset !important;
    }

    .customHoverButton {
        background-color: unset !important;
        color: var(--v-primary-base);
    }

        .customHoverButton:hover {
            filter: brightness(80%);
        }
</style>
